import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Smartphone, Check, RefreshCw, PartyPopper, ArrowRight, ArrowLeft, Gift } from "lucide-react";

const key = (id) => `flexline_esim_onboarded_${id}`;

export function EsimOnboarding({ subId, qrLpa }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (qrLpa && subId && !localStorage.getItem(key(subId))) {
            const t = setTimeout(() => setOpen(true), 700);
            return () => clearTimeout(t);
        }
    }, [subId, qrLpa]);

    const dismiss = () => {
        if (subId) localStorage.setItem(key(subId), "1");
        setOpen(false);
    };

    const appleUrl = `https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(qrLpa || "")}`;
    const androidUrl = `https://esimsetup.android.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(qrLpa || "")}`;

    const steps = [
        {
            icon: Smartphone,
            title: "Install your eSIM",
            body: "On the phone you want to use FlexLine on, tap your device's button below. It opens the Add-eSIM screen — no QR scan needed.",
            content: (
                <div className="flex flex-wrap gap-2 pt-1">
                    <a href={appleUrl} target="_blank" rel="noopener noreferrer" data-testid="onboard-install-iphone"
                        className="inline-flex items-center gap-2 bg-[#4C1D95] text-white rounded-sm px-4 h-10 text-sm font-medium hover:bg-[#4C1D95]/90">
                        <Smartphone className="w-4 h-4" /> Install on iPhone
                    </a>
                    <a href={androidUrl} target="_blank" rel="noopener noreferrer" data-testid="onboard-install-android"
                        className="inline-flex items-center gap-2 bg-brand-green text-white rounded-sm px-4 h-10 text-sm font-medium hover:opacity-90">
                        <Smartphone className="w-4 h-4" /> Install on Android
                    </a>
                </div>
            ),
        },
        {
            icon: Check,
            title: "Confirm on your phone",
            body: "Your phone will ask to add the FlexLine data plan. Tap Continue / Add eSIM and follow the prompts. Label it \"FlexLine\" so it's easy to spot.",
        },
        {
            icon: RefreshCw,
            title: "Restart your device",
            body: "Once installed, restart your phone so it connects to the FlexLine network. Make sure the FlexLine line is set as your data line.",
        },
        {
            icon: PartyPopper,
            title: "You're connected! 🎉",
            body: "Data is live and you can make calls and send texts right here in the FlexLine app. If you're porting a number, it'll switch over automatically on your transfer date.",
            content: (
                <button
                    onClick={() => { dismiss(); navigate("/referrals"); }}
                    data-testid="onboard-refer-cta"
                    className="mt-1 w-full inline-flex items-center justify-center gap-2 bg-brand-green text-white rounded-sm px-4 h-11 text-sm font-medium hover:opacity-90"
                >
                    <Gift className="w-4 h-4" /> Refer friends &amp; earn rewards
                </button>
            ),
        },
    ];

    const s = steps[step];
    const Icon = s.icon;
    const last = step === steps.length - 1;

    useEffect(() => {
        if (open && last && subId) {
            api.post(`/mobile/subscription/${subId}/welcome-nudge`).catch(() => {});
        }
    }, [open, last, subId]);

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
            <DialogContent className="max-w-md" data-testid="esim-onboarding-modal">
                <DialogHeader>
                    <div className="w-12 h-12 rounded-sm bg-[#F5F0FF] grid place-items-center mb-2">
                        <Icon className="w-6 h-6 text-[#4C1D95]" />
                    </div>
                    <DialogTitle className="font-display text-2xl font-light">{s.title}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-zinc-600 leading-relaxed" data-testid="onboard-body">{s.body}</p>
                {s.content}

                <div className="flex items-center gap-1.5 pt-2" data-testid="onboard-progress">
                    {steps.map((_, i) => (
                        <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#4C1D95]" : i < step ? "w-3 bg-[#4C1D95]/40" : "w-3 bg-zinc-200"}`} />
                    ))}
                </div>

                <div className="flex items-center justify-between pt-2">
                    {step > 0 ? (
                        <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="onboard-back" className="rounded-sm h-9 text-xs border-[#DDD6FE]">
                            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
                        </Button>
                    ) : (
                        <button onClick={dismiss} data-testid="onboard-skip" className="text-xs text-zinc-500 underline">Skip for now</button>
                    )}
                    {last ? (
                        <Button onClick={dismiss} data-testid="onboard-finish" className="bg-[#4C1D95] text-white rounded-sm h-9 text-xs">Done</Button>
                    ) : (
                        <Button onClick={() => setStep(step + 1)} data-testid="onboard-next" className="bg-[#4C1D95] text-white rounded-sm h-9 text-xs">
                            Next <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
